import { HStack, VStack } from '@chakra-ui/react';
import React from 'react';
import { useFormContext } from 'react-hook-form';

import { AppError } from '../../../../../framework/models/errors';
import { TypeUtils } from '../../../../../framework/utils/UtilityTypes';
import { BackButton, SaveButton } from '../../../../components/StyledButton';
import { pagePaths } from '../../../../constants/Page';
import { useGlobalStateMachine } from '../../../../GlobalStore';
import { useEngeneNavigate } from '../../../../hooks/navigate';
import { usePostOutsourceMember } from '../../../../hooks/queries/outsourceMember';
import useToastHook from '../../../../hooks/ToastHook';
import { Worker } from '../../../../models/gen/worker';
import { Formatter } from '../../../../utils/FormatUtils';
import { Validator } from '../../../../utils/ValidationUtils';
import { ZipCodeUtils } from '../../../../utils/ZipCode';
import { useConfirmModal } from '../../../modals/confirm';
import { useSizeContext } from '../../../SizeProvider';
import { UserInfoAccordion } from '../../user-info/accordion/UserInfoAccordion';

/**
 * 外注メンバ画面 新規作成モード
 * @returns
 */
const OutsourcerWorkerCreate: React.FunctionComponent = () => {
  const { actions, state } = useGlobalStateMachine();
  const navigate = useEngeneNavigate();
  const sizes = useSizeContext();

  const { ConfirmModal, onOpen } = useConfirmModal<Worker>();
  const form = useFormContext();
  const { toast } = useToastHook();
  const postOutsourcerMember = usePostOutsourceMember();

  return (
    <VStack
      noValidate
      as="form"
      onSubmit={form.handleSubmit((initialState: Worker) => {
        // 氏名(必須)
        if (TypeUtils.isNilString(initialState?.name)) {
          toast('氏名を入力してください');
          return;
        }
        // カナ氏名(必須＋全角カナ)
        if (TypeUtils.isNilString(initialState?.nameK)) {
          toast('氏名 (カナ)を入力してください');
          return;
        } else if (!Validator.isZenkakuKatakana(initialState?.nameK)) {
          toast('氏名 (カナ)は全角カタカナと全角スペースで入力してください');
          return;
        }
        // 性別(必須)
        if (!TypeUtils.isNonNilStrict(initialState?.gender)) {
          toast('性別を入力してください');
          return;
        }
        // 開始日(必須)
        if (TypeUtils.isNilString(initialState?.dateFrom)) {
          toast('開始日を入力してください');
          return;
        }
        // 終了日(必須)
        if (TypeUtils.isNilString(initialState?.dateTo)) {
          toast('終了日を入力してください');
          return;
        }
        // 開始日<=終了日
        if (
          !TypeUtils.isNilString(initialState?.dateFrom) &&
          !TypeUtils.isNilString(initialState?.dateTo) &&
          !Validator.lessThanDate(initialState?.dateFrom, initialState?.dateTo)
        ) {
          toast('終了日は開始日以降となるよう入力してください');
          return;
        }

        // メールアドレス(Email)
        if (
          TypeUtils.isNonNilString(initialState?.mailAddr) &&
          !Validator.isValidEmail(initialState?.mailAddr)
        ) {
          toast('不正なメールアドレスです', 'warning');
          return;
        }

        if (
          TypeUtils.isNonNilString(initialState.adderssInfo?.zipCode) &&
          !ZipCodeUtils.isValidNoHyphen(initialState.adderssInfo?.zipCode)
        ) {
          toast('郵便番号は半角数値7桁で入力してください', 'warning');
          console.warn(
            '郵便番号は半角数値7桁で入力してください',
            initialState.adderssInfo?.zipCode
          );
          return;
        }
        if (
          TypeUtils.isNonNilString(initialState.addressInfoOffamily?.zipCode) &&
          !ZipCodeUtils.isValidNoHyphen(
            initialState.addressInfoOffamily?.zipCode
          )
        ) {
          toast(
            '郵便番号(家族連絡先)は半角数値7桁で入力してください',
            'warning'
          );
          console.warn(
            '郵便番号(家族連絡先)は半角数値7桁で入力してください',
            initialState.addressInfoOffamily?.zipCode
          );
          return;
        }
        // 血圧(最高)>=血圧(最低) ※片方だけ入力を許す
        // ※編集した値は文字列として取得されることを考慮する必要あり
        if (
          TypeUtils.isNonNilStrict(
            initialState?.workerReference?.bloodPressureMax
          ) &&
          TypeUtils.isNonNilStrict(
            initialState?.workerReference?.bloodPressureMin
          ) &&
          !Validator.lessThan(
            initialState?.workerReference?.bloodPressureMin as number | string,
            initialState?.workerReference?.bloodPressureMax as number | string
          )
        ) {
          toast('血圧(最高)は血圧(最低)以上となるよう入力してください');
          return;
        }

        onOpen(initialState);
      })}
      width="100%"
    >
      <UserInfoAccordion
        create
        defaultIndex={state.pageInfo.USER_INFO?.openedPanel || 0}
        isOutsourcer
        mode="edit"
      />
      <SaveButton />
      <HStack width={sizes.width.styledMessageList}>
        <BackButton onClick={() => navigate(-1)} />
      </HStack>
      <ConfirmModal
        message="保存してよろしいですか?"
        onOk={(worker) => {
          if (TypeUtils.isNonNil(worker)) {
            actions.setGuard(true);
            postOutsourcerMember.mutate(
              {
                ...worker,
                gender: worker.gender || 0,
                dateOfBirth: Formatter.date(worker.dateOfBirth, true),
                hiredDate: Formatter.date(worker.hiredDate, true),
                firstDateOfPayRaise: Formatter.date(
                  worker.firstDateOfPayRaise,
                  true
                ),
                dateFrom: Formatter.date(worker.dateFrom, true),
                dateTo: Formatter.date(worker.dateTo, true, '9999-12-31'),
                adderssInfo: {
                  ...worker.adderssInfo,
                  zipCode: ZipCodeUtils.removeHyphen(
                    worker.adderssInfo?.zipCode
                  ),
                },
                addressInfoOffamily: {
                  ...worker.addressInfoOffamily,
                  zipCode: ZipCodeUtils.removeHyphen(
                    worker.addressInfoOffamily?.zipCode
                  ),
                },
                workerReference: {
                  ...worker.workerReference,
                  chiefFlag: worker.workerReference?.chiefFlag || false,
                  experienceOffsetMonths:
                    worker.workerReference?.experienceOffsetMonths || 0,
                  startDateOfExperience: Formatter.date(
                    worker.workerReference?.startDateOfExperience,
                    true
                  ),
                  lastHealthCheckDate: Formatter.date(
                    worker.workerReference?.lastHealthCheckDate,
                    true
                  ),
                  specialHealthCheckDate: Formatter.date(
                    worker.workerReference?.specialHealthCheckDate,
                    true
                  ),
                },
              },
              {
                onSuccess: () => {
                  navigate(pagePaths.WORKER_MANAGEMENT);
                  actions.setGuard(false);
                },
                onError: (error) => {
                  toast(
                    error instanceof AppError
                      ? error
                      : '外注メンバの作成に失敗しました'
                  );
                  actions.setGuard(false);
                },
              }
            );
          }
        }}
      />
    </VStack>
  );
};

export default OutsourcerWorkerCreate;
